//@flow
import * as Immutable from 'immutable';

import {
    RiderPersona,
    Competency
} from 'common/models/flow/MasterRiderPersona';
import type {
    RiderPersonaModelType,
    CompetencyModelType
} from 'common/models/flow/MasterRiderPersona';

import { TransporterId } from './TransporterId';
import { MasterTransporter } from './MasterTransporter';
import { SkillId } from './SkillId';

import { AuditMetadata } from './AuditMetadata';

import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';

import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import { RiderPersonaLimits } from './RiderPersonaLimits';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';
import { VolumeUnitEnum } from 'common/enums/VolumeUnitEnum';

import DataUtils from 'common/utils/DataUtils';
// import NumberUtils from 'utils/NumberUtils';
import RegExUtil from 'common/utils/RegExUtil';

import I18nHelper from 'common/helpers/I18nHelper';

const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

class InternalCompetency extends Competency {
    static fromJS(json: CompetencyModelType): InternalCompetency {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

export { InternalCompetency as Competency };

export const RiderPersonaTypeEnum = new EntityTypeEnum({
    PJP: 'PJP',
    DELIVERY: 'DELIVERY',
    SERVICE: 'SERVICE',
    NONE: 'NONE'
});

export const FleetTypeEnum = new EntityTypeEnum({
    CAPTIVE: 'CAPTIVE',
    THIRD_PARTY: 'THIRD_PARTY',
    ADHOC: 'ADHOC'
});

const ENTITY_NAME_ID = 'masterRiderPersona';

export const validateRiderPersonaType = riderPersonaType => {
    const riderPersonaTypeValues = RiderPersonaTypeEnum.values();
    const riderGroup = nsTranslate('masterRiderPersonaProperties.type');
    if (DataUtils.isEmpty(riderPersonaType)) {
        return nsTranslate('entityValidationErrors.empty', {
            fieldType: riderGroup
        });
    }
    if (
        !DataUtils.isEmpty(riderPersonaType) &&
        (!riderPersonaType.trim() ||
            !riderPersonaTypeValues.includes(riderPersonaType))
    ) {
        return nsTranslate('entityValidationErrors.oneOfType', {
            typeList: riderPersonaTypeValues.join(', '),
            fieldType: riderGroup
        });
    }
    return null;
};

export const validateFleetType = fleetType => {
    let validationMessage = null;
    const fleetTypeEnumValues = FleetTypeEnum.values();

    if (DataUtils.isEmpty(fleetType)) {
        validationMessage = nsTranslate(
            'validations:riderPersonaValidationErrors.fleetTypeEmpty'
        );
    } else if (
        !DataUtils.isEmpty(fleetType) &&
        (!fleetType.trim() || !fleetTypeEnumValues.includes(fleetType))
    ) {
        validationMessage = nsTranslate(
            'validations:riderPersonaValidationErrors.fleetTypeEnumValidation',
            {
                typeList: fleetTypeEnumValues.join(', '),
                entityNameId: ENTITY_NAME_ID
            }
        );
    }
    return validationMessage;
};

const validateBoolean = (value: any) => {
    if (!DataUtils.isEmpty(value) && !DataUtils.isBoolean(value)) {
        return nsTranslate(
            'validations:riderPersonaValidationErrors.booleanValueValidation',
            {
                positiveLabel: nsTranslate(
                    'entityManagement:masterVehicleModelProperties.boolean.yes'
                ),
                negativeLabel: nsTranslate(
                    'entityManagement:masterVehicleModelProperties.boolean.no'
                )
            }
        );
    }
};

@validatable
class InternalRiderPersona extends RiderPersona {
    static fromJS(json: RiderPersonaModelType): InternalRiderPersona {
        const state: Object = Object.assign({}, json);
        state.transporterId = state.transporterId
            ? TransporterId.fromJS(state.transporterId)
            : state.transporterId;
        state.transporter = state.transporter
            ? MasterTransporter.fromJS(state.transporter)
            : state.transporter;
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.limits = state.limits
            ? RiderPersonaLimits.fromJS(state.limits)
            : state.limits;
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        state.competency = state.competency
            ? InternalCompetency.fromJS(state.competency)
            : state.competency;
        return new this(Immutable.Map(state));
    }

    get transporterId(): ?TransporterId {
        return this._state.get('transporterId');
    }

    get transporter(): ?MasterTransporter {
        return this._state.get('transporter');
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get limits(): ?RiderPersonaLimits {
        return this._state.get('limits');
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (DataUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            DataUtils.isEmpty(this.name) ||
            DataUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setType(type: string): this {
        return super.setType(type);
    }

    validateType(): boolean {
        if (this.enablePjp) {
            return validateRiderPersonaType(this.type);
        }
        return null;
    }

    @validate
    setFleetType(fleetType: string): this {
        return super.setFleetType(fleetType);
    }

    validateFleetType() {
        return validateFleetType(this.fleetType);
    }

    @validate
    setAllowMultiTrip(allowMultiTrip: string): this {
        return super.setAllowMultiTrip(allowMultiTrip);
    }

    validateAllowMultiTrip() {
        return validateBoolean(this.allowMultiTrip);
    }

    @validate
    setTransporterId(transporterId: Object): this {
        return super.setTransporterId(transporterId);
    }

    validateTransporterId() {
        const { transporterId } = this;
        if (DataUtils.isEmpty(transporterId?.transporterId)) {
            return nsTranslate(
                'validations:riderPersonaValidationErrors.transporterIdEmpty'
            );
        }
    }

    @validate
    setEnableAuth(auth: boolean): this {
        return super.setEnableAuth(auth);
    }

    validateEnableAuth(): ?string {
        const { enableAuth, enableLotr } = this;
        if (enableAuth && !enableLotr) {
            return nsTranslate(
                'validations:riderPersonaValidationErrors.lotrNotEnabled'
            );
        }
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    @validateAll
    setAllValidationErrors(): this {
        let { limits } = this;
        if (limits) {
            limits = limits.setAllValidationErrors();
        }
        return this.setLimits(limits);
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { limits } = this;
        if (limits?.hasValidationErrors?.()) {
            return true;
        }
        return this.validateId() || this.validateName();
    }

    sanitize(): this {
        return this;
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize() {
        let sanitizedRiderPersonaModel = this.sanitize();
        sanitizedRiderPersonaModel = super.defaultSanitize(
            sanitizedRiderPersonaModel
        );
        if (sanitizedRiderPersonaModel) {
            const sanitizedRiderPersonaLimits = sanitizedRiderPersonaModel.limits?.defaultSanitize();
            sanitizedRiderPersonaModel = sanitizedRiderPersonaModel
                .setLimits(sanitizedRiderPersonaLimits)
                .setVolumeUnit(null); // This field is for FE only
        }
        return sanitizedRiderPersonaModel;
    }

    static defaultInit({ props }): InternalRiderPersona {
        const riderPersonaLimits = RiderPersonaLimits.defaultInit();
        const arePjpFieldsDisabled = props.isFieldHidden?.();
        return InternalRiderPersona.fromJS({
            allowMultiTrip: !arePjpFieldsDisabled,
            type: arePjpFieldsDisabled ? RiderPersonaTypeEnum.DELIVERY : null,
            enableAuth: arePjpFieldsDisabled || null,
            enableLotr: arePjpFieldsDisabled || null
        })
            .setLimits(riderPersonaLimits)
            .setVolumeUnit(
                props.taskSettings.defaultVolumeLimit.unit ||
                    VolumeUnitEnum.MetersCubic
            );
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    get competency(): ?InternalCompetency {
        return this._state.get('competency');
    }
}

export { InternalRiderPersona as RiderPersona };
